import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { httpOk, GET } from "helpers/api"
import { formatDate, formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildAnalyticsUrl, formatPeriodLabel } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "14px",
	boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
}

const mutedStyle = { color: "var(--dark-text-muted, #8791a1)", fontSize: "12px" }
const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }

// Погоду показываем только реальную, из Open-Meteo; без неё — ничего не рисуем
function hasWeather(item) {
	return item?.weather_source === "open-meteo" && !!item.weather_type
}

function formatDateTime(value) {
	if (!value) return ""
	return formatDate(String(value).replace(" ", "T"), "dd.MM.yyyy HH:mm")
}

function formatDateLabel(date) {
	if (!date) return ""
	const parts = String(date).split("-")
	const month = parts.length === 3 ? parts[1] : parts[0]
	const day = parts.length === 3 ? parts[2] : parts[1]
	return `${day}.${month}`
}

function confidenceColor(value) {
	const number = Number(value || 0)
	if (number >= 90) return "#16a34a"
	if (number >= 80) return "#e0762c"
	return "#dc2626"
}

// Точность прогноза по бэктесту; null — не на чем проверить
function Accuracy({ value, style }) {
	const { t } = useTranslation()
	if (value === null || value === undefined) {
		return <span style={{ ...mutedStyle, ...style }} title={t("ai_forecast_low_history")}>—</span>
	}
	return <span style={{ color: confidenceColor(value), fontWeight: 900, ...style }}>{formatMoney(value, 0)}%</span>
}

function weatherIcon(type) {
	if (type === "rain") return <i className="uil uil-cloud-rain" style={{ color: "var(--dark-fg-primary, #2563eb)" }}></i>
	if (type === "cloud") return <i className="uil uil-cloud" style={{ color: "var(--dark-text-muted, #64748b)" }}></i>
	if (type === "sun") return <i className="uil uil-sun" style={{ color: "#f59f00" }}></i>
	return null
}

// Плашка о состоянии AI: модель не ответила (нет ключа, таймаут, мусор) — показаны правила
function AiStatus({ data }) {
	const { t } = useTranslation()
	if (!data || (!data.ai_fallback && data.ai_enabled !== false)) return null
	const reason = data.ai_enabled === false ? t("ai_not_connected") : t("ai_unavailable_now")
	return (
		<div className="alert alert-warning d-flex align-items-center gap-2 mb-3" style={{ borderRadius: "12px" }}>
			<i className="uil uil-info-circle"></i>
			<span>{t("ai_fallback_notice", { reason })}</span>
		</div>
	)
}

function EventCard({ item }) {
	const color = item.impact === "high" ? "#dc2626" : item.impact === "medium" ? "#e0762c" : "#2563eb"
	return (
		<div style={{ background: `${color}12`, border: `1px solid ${color}25`, borderRadius: "14px", padding: "12px" }}>
			<div style={{ fontWeight: 900, color }}>{item.title}</div>
			<div style={{ ...mutedStyle, color: "var(--dark-text, #374151)" }}>{item.description}</div>
		</div>
	)
}

function DayBadge({ day }) {
	const weather = hasWeather(day)
	if (!weather && !day.event_title) return null

	return (
		<div className="d-flex justify-content-center align-items-center gap-1 mt-1" style={mutedStyle} title={day.impact || day.weather_label || day.event_title}>
			{weather && weatherIcon(day.weather_type)}
			{weather && day.weather_label && <span>{day.weather_label}</span>}
			{day.event_title && <i className="uil uil-calendar-alt" style={{ color: "#e0762c" }} title={day.event_title}></i>}
		</div>
	)
}

function ForecastTable({ data }) {
	const { t } = useTranslation()
	const branches = data?.branches || []
	const totals = data?.totals
	const days = totals?.days || branches[0]?.days || []
	const todayDate = data?.forecast_start_date

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
					<div>
						<h5 className="mb-0" style={{ fontWeight: 900 }}>{t("ai_forecast_by_branches")}</h5>
						<div style={mutedStyle}>{t("ai_forecast_horizon")}</div>
						{data?.weather_available === false && (
							<div style={mutedStyle}>{t("ai_forecast_weather_unavailable")}</div>
						)}
					</div>
					<div className="text-end">
						<div style={{ fontWeight: 900 }}>{totals?.total_revenue_short || "0"}</div>
						<div style={mutedStyle}>
							{t("ai_forecast_network_accuracy")}: <Accuracy value={data?.accuracy_percent} style={{ fontSize: "12px" }} />
						</div>
						{data?.method && <div style={mutedStyle}>{t("ai_forecast_method")}: {data.method}</div>}
					</div>
				</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>{t("ai_col_branch")}</th>
								{days.map(day => (
									<th className="py-3 text-muted fw-semibold text-center" style={headerThStyle} key={day.date}>
										{day.weekday}<br />
										<span style={mutedStyle}>{formatDateLabel(day.date)}</span>
										{(day.is_today || day.date === todayDate) && <div style={{ color: "var(--bs-primary)", fontSize: "12px", fontWeight: 800 }}>{t("ai_forecast_today")}</div>}
									</th>
								))}
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("ai_total")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle} title={t("ai_forecast_accuracy_title")}>{t("ai_forecast_accuracy_short")}</th>
							</tr>
						</thead>
						<tbody>
							{branches.length === 0 && <tr><td colSpan={days.length + 3} className="text-center text-muted py-4">{t("ai_forecast_no_data")}</td></tr>}
							{branches.map(branch => (
								<tr key={branch.pos_id}>
									<td className="py-3" style={{ fontWeight: 800 }}>
										<div>{branch.branch_name}</div>
										{branch.map_address && <div style={mutedStyle}>{branch.map_address}</div>}
									</td>
									{branch.days.map(day => (
										<td className="py-3 text-center" key={day.date}>
											<div style={{ fontWeight: 800 }}>{day.revenue_short}</div>
											<div style={mutedStyle}>
												{formatMoney(day.cheques, 0)} {t("ai_forecast_cheques_short")} · <Accuracy value={day.confidence_percent} style={{ fontSize: "12px", fontWeight: 700 }} />
											</div>
											<DayBadge day={day} />
										</td>
									))}
									<td className="py-3 text-end" style={{ fontWeight: 900 }}>{branch.total_revenue_short}</td>
									<td className="py-3 text-end"><Accuracy value={branch.accuracy_percent} /></td>
								</tr>
							))}
							{branches.length > 0 && (
								<tr>
									<td className="py-3" style={{ fontWeight: 900 }}>{t("ai_total")}</td>
									{days.map(day => <td className="py-3 text-center" style={{ fontWeight: 900 }} key={day.date}>{day.revenue_short}</td>)}
									<td className="py-3 text-end" style={{ fontWeight: 900 }}>{totals?.total_revenue_short}</td>
									<td className="py-3 text-end"><Accuracy value={data?.accuracy_percent} /></td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function EventsWeatherTable({ rows = [] }) {
	const { t } = useTranslation()
	// Строка нужна, только если есть событие или настоящая погода
	const visibleRows = rows.filter(row => row.event_title || hasWeather(row))

	return (
		<div className="card mt-3" style={cardStyle}>
			<div className="card-body">
				<div className="mb-3">
					<h5 className="mb-0" style={{ fontWeight: 900 }}>{t("ai_events_weather")}</h5>
					<div style={mutedStyle}>{t("ai_events_weather_hint")}</div>
				</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>{t("ai_col_branch")}</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>{t("ai_col_date")}</th>
								<th className="py-3 text-muted fw-semibold text-center" style={headerThStyle}>{t("ai_col_weather")}</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>{t("ai_col_event")}</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>{t("ai_col_impact")}</th>
							</tr>
						</thead>
						<tbody>
							{visibleRows.length === 0 && <tr><td colSpan="5" className="text-center text-muted py-4">{t("ai_events_weather_empty")}</td></tr>}
							{visibleRows.map(row => (
								<tr key={`${row.pos_id}-${row.date}`}>
									<td className="py-3" style={{ fontWeight: 800 }}>
										<div>{row.branch_name}</div>
										{row.map_address && <div style={mutedStyle}>{row.map_address}</div>}
									</td>
									<td className="py-3" style={{ fontWeight: 800 }}>{row.weekday} · {formatDateLabel(row.date)}</td>
									<td className="py-3 text-center">
										{hasWeather(row) ? (
											<>
												<div style={{ fontSize: "18px" }}>{weatherIcon(row.weather_type)}</div>
												<div style={mutedStyle}>{row.weather_label}</div>
											</>
										) : <span style={mutedStyle}>—</span>}
									</td>
									<td className="py-3">{row.event_title || "—"}</td>
									<td className="py-3">{row.impact || "—"}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function Index() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)

	// Периода нет: горизонт прогноза — 7 дней с сегодня
	const filters = useAnalyticsFilters({
		pos_id: -1,
		order_type_ids: [],
		source_ids: [],
	}, searchWithFilter)
	const options = useAnalyticsOptions(["sources"], filters.draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/ai-forecast", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	const horizon = formatPeriodLabel(data?.forecast_start_date, data?.forecast_end_date)

	return (
		<>
			<AnalyticsFilterBar
				title={t("ai_forecast_title")}
				icon="uil-chart-growth"
				subtitle={horizon ? t("ai_forecast_subtitle_period", { period: horizon }) : t("ai_forecast_subtitle_default")}
				filters={filters}
				presets={false}
			>
				<StandardFields filters={filters} options={options} />
			</AnalyticsFilterBar>

			<AiStatus data={data} />

			<div className="row g-3 mb-3">
				<div className="col-lg-8">
					<div className="card h-100" style={cardStyle}>
						<div className="card-body">
							<h5 className="mb-1" style={{ fontWeight: 900 }}>{t("ai_conclusion")}</h5>
							<div style={{ color: "var(--dark-text, #374151)" }}>{data?.summary || t("ai_no_data")}</div>
							{data?.accuracy_note && <div className="mt-2" style={mutedStyle}>{data.accuracy_note}</div>}
							{data?.generated_at && (
								<div className="mt-2" style={mutedStyle}>
									{t("ai_generated_at", { date: formatDateTime(data.generated_at) })}
									{data.ai_cached && ` · ${t("ai_cached_note")}`}
								</div>
							)}
						</div>
					</div>
				</div>
				<div className="col-lg-4">
					<div className="card h-100" style={cardStyle}>
						<div className="card-body">
							<h5 className="mb-2" style={{ fontWeight: 900 }}>{t("ai_recommendations")}</h5>
							<div className="d-flex flex-column gap-2">
								{(data?.recommendations || []).length === 0 && <div style={mutedStyle}>{t("ai_no_recommendations")}</div>}
								{(data?.recommendations || []).map((item, index) => <div key={index} style={{ background: "var(--dark-surface-2, #f8fafc)", borderRadius: "12px", padding: "9px 10px" }}>{item}</div>)}
							</div>
						</div>
					</div>
				</div>
			</div>

			{(data?.events || []).length > 0 && (
				<div className="row g-3 mb-3">
					{data.events.map((item, index) => <div className="col-md-4" key={index}><EventCard item={item} /></div>)}
				</div>
			)}

			<ForecastTable data={data} />
			<EventsWeatherTable rows={data?.events_weather || []} />
		</>
	)
}

export default Index
